import { RuleTemplatesListSortColumns } from '@retracedgmbh/modules/risk-analysis/contracts';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { fetchRiskRules } from '@/api/risk';
import type { RuleView } from '@/api/risk/types';
import { useTable } from '@/hooks/useTable';

interface Options {
  /** Which slice of the catalogue to list. */
  view: RuleView;
  /**
   * Where this table's preferences live. Required rather than defaulted: the
   * client rules page and the management one are different tables with
   * different columns, and sharing a key would have each restore the other's
   * layout.
   */
  storageKey: string;
  /**
   * The tab this table sits under, so its saved state is only restored there.
   * Omitted by a host that gives each tab its own route — there is no `?tab=`
   * to compare against.
   */
  tab?: string;
  /**
   * Restrict the list to published templates.
   *
   * The client rules page wants this on its Retraced tab — a customer has no
   * use for a draft they cannot see. The management screen wants the opposite:
   * drafts are exactly what it exists to work on, and the old page sent no
   * such filter. It was hard-coded to the `RETRACED` view here, which hid
   * every unpublished template from management.
   */
  activeOnly?: boolean;
}

/**
 * The rule-template list, for any screen that shows one.
 *
 * Shared because `/risk/rules` and the management risk screen list the same
 * `/api/risk-analysis/rule-templates` endpoint, differing only in which `view` they ask for
 * and how they identify their own table.
 */
/** What a rule-template list asks for, independent of where it reads it from. */
export interface RiskRulesQueryInput {
  view: RuleView;
  /** 1-based, as the URL carries it. */
  page: number;
  limit: number;
  sort?: string | undefined;
  filters?: Record<string, string[]> | undefined;
  activeOnly?: boolean;
}

const SORTABLE_COLUMNS = new Set<string>(RuleTemplatesListSortColumns);

/**
 * The endpoint rejects a sort on a column it cannot order by. A sort saved in
 * the table's preferences or the URL can still name one — the tags column
 * used to look sortable — so those segments are dropped rather than sent.
 */
const sortableOnly = (sort: string | undefined): string | undefined => {
  const kept = (sort ?? '')
    .split(',')
    .filter(segment => SORTABLE_COLUMNS.has(segment.split(':')[0] ?? ''));
  return kept.length > 0 ? kept.join(',') : undefined;
};

/**
 * Shared by the hook and by the route loaders that prefetch it — built from one
 * input so both land on the same cache entry.
 */
export function riskRulesQueryOptions({
  view,
  page,
  limit,
  sort,
  filters,
  activeOnly = false,
}: RiskRulesQueryInput) {
  return queryOptions({
    // Normalised: a loader reading the URL has `filters` undefined where the
    // hook has `{}`, and `hashKey` drops undefined — so the prefetch would land
    // on a different entry than the read.
    queryKey: [
      'risk',
      'rules',
      view,
      activeOnly,
      { page, limit, sort: sort ?? null, filters: filters ?? {} },
    ] as const,
    queryFn: () => {
      const qs = new URLSearchParams();
      qs.set('page', String(Math.max(0, page - 1)));
      qs.set('count', String(limit));
      qs.set('view', view);
      const apiSort = sortableOnly(sort);
      if (apiSort) qs.set('sort', apiSort);
      const filterObj: Record<string, unknown> = {};
      if (activeOnly) filterObj.isActive = 1;
      for (const [k, v] of Object.entries(filters ?? {})) {
        if (v.length > 0) filterObj[k] = v.join(',');
      }
      qs.set('filter', JSON.stringify(filterObj));
      return fetchRiskRules(qs.toString());
    },
    staleTime: 30_000,
  });
}

export function useRiskRulesList({
  view,
  storageKey,
  tab,
  activeOnly = false,
}: Options) {
  const table = useTable(storageKey, {
    mode: 'url',
    ...(tab !== undefined ? { tab } : {}),
  });

  const query = useQuery({
    ...riskRulesQueryOptions({
      view,
      page: table.page,
      limit: table.limit,
      ...(table.queryParams.sort !== undefined
        ? { sort: table.queryParams.sort }
        : {}),
      filters: table.filters,
      activeOnly,
    }),
    placeholderData: prev => prev,
  });

  return {
    ...query,
    rows: query.data?.data ?? [],
    total:
      query.data?.meta?.totalCount ??
      (query.data?.meta as { total?: number })?.total ??
      0,
    sorting: table.sorting,
    filters: table.filters,
    pagination: { pageIndex: table.page - 1, pageSize: table.limit },
    onSortingChange: table.onSortingChange,
    onFiltersChange: table.onFiltersChange,
    onPaginationChange: table.onPaginationChange,
  };
}
